import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const message = error.response?.data?.message || 'Something went wrong';
    const status = error.response?.status;

    // 401 trên các API thực tế (không phải getMe khi verify) → forceLogout
    if (status === 401) {
      localStorage.removeItem('token');
      // Dispatch event để store xử lý, tránh circular import
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }

    return Promise.reject({ message, status });
  }
);

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  updateDetails: (data) => api.put('/auth/updatedetails', data),
  updatePassword: (data) => api.put('/auth/updatepassword', data),
  updatePreferences: (data) => api.put('/auth/preferences', data),
  addBookmark: (novelId) => api.post(`/auth/bookmarks/${novelId}`),
  removeBookmark: (novelId) => api.delete(`/auth/bookmarks/${novelId}`),
  updateHistory: (novelId, chapterId) => api.post(`/auth/history/${novelId}/${chapterId}`),
  getAllUsers: (params) => api.get('/auth/users', { params }),
  updateUserRole: (userId, role) => api.put(`/auth/users/${userId}/role`, { role }),
};
export const novelAPI = {
  getAll: (params) => api.get('/novels', { params }),
  getBySlug: (slug) => api.get(`/novels/${slug}`),
  getById: (id) => api.get(`/novels/id/${id}`),
  getChapters: (slug, params) => api.get(`/novels/${slug}/chapters`, { params }),
  getHot: (params) => api.get('/novels/hot', { params }),
  getFeatured: () => api.get('/novels/featured'),
  getRecent: (params) => api.get('/novels/recent', { params }),
  search: (params) => api.get('/novels/search', { params }),
  create: (data) => api.post('/novels', data),
  update: (id, data) => api.put(`/novels/${id}`, data),
  delete: (id) => api.delete(`/novels/${id}`),
};
export const chapterAPI = {
  get: (novelSlug, chapterNumber) => api.get(`/chapters/${novelSlug}/${chapterNumber}`),
  getById: (id) => api.get(`/chapters/${id}`),
  getList: (novelId, params) => api.get(`/chapters/list/${novelId}`, { params }),
  create: (data) => api.post('/chapters', data),
  createBulk: (data) => api.post('/chapters/bulk', data),
  update: (id, data) => api.put(`/chapters/${id}`, data),
  delete: (id) => api.delete(`/chapters/${id}`),
};
export const genreAPI = {
  getAll: () => api.get('/genres'),
  getBySlug: (slug) => api.get(`/genres/${slug}`),
  getPopular: (params) => api.get('/genres/popular', { params }),
  create: (data) => api.post('/genres', data),
  update: (id, data) => api.put(`/genres/${id}`, data),
  delete: (id) => api.delete(`/genres/${id}`),
};
export const commentAPI = {
  getNovelComments: (novelId, params) => api.get(`/comments/novel/${novelId}`, { params }),
  getChapterComments: (chapterId, params) => api.get(`/comments/chapter/${chapterId}`, { params }),
  getReplies: (commentId, params) => api.get(`/comments/${commentId}/replies`, { params }),
  create: (data) => api.post('/comments', data),
  update: (id, data) => api.put(`/comments/${id}`, data),
  delete: (id) => api.delete(`/comments/${id}`),
  toggleLike: (id) => api.post(`/comments/${id}/like`),
  togglePin: (id) => api.post(`/comments/${id}/pin`),
};
export const ratingAPI = {
  getNovelRatings: (novelId, params) => api.get(`/ratings/novel/${novelId}`, { params }),
  getMyRating: (novelId) => api.get(`/ratings/novel/${novelId}/me`),
  createOrUpdate: (data) => api.post('/ratings', data),
  delete: (id) => api.delete(`/ratings/${id}`),
  toggleLike: (id) => api.post(`/ratings/${id}/like`),
};
export const domainAPI = {
  getAll: () => api.get('/domains'),
  getCurrent: () => api.get('/domains/current'),
  getById: (id) => api.get(`/domains/${id}`),
  create: (data) => api.post('/domains', data),
  update: (id, data) => api.put(`/domains/${id}`, data),
  delete: (id) => api.delete(`/domains/${id}`),
};
export const crawlerAPI = {
  getSources: () => api.get('/crawler/sources'),
  getNovelInfo: (url) => api.post('/crawler/novel-info', { url }),
  getChapter: (url) => api.post('/crawler/chapter', { url }),
  importNovel: (data) => api.post('/crawler/import-novel', data),
  importChapters: (data) => api.post('/crawler/import-chapters', data),
};
export const adAPI = {
  getAll: (params) => api.get('/ads', { params }),
  getById: (id) => api.get(`/ads/${id}`),
  getByPosition: (position) => api.get(`/ads/position/${position}`),
  getAllForDomain: () => api.get('/ads/all'),
  getStats: (params) => api.get('/ads/stats', { params }),
  create: (data) => api.post('/ads', data),
  update: (id, data) => api.put(`/ads/${id}`, data),
  delete: (id) => api.delete(`/ads/${id}`),
  trackClick: (id) => api.post(`/ads/${id}/click`),
  duplicate: (id, data) => api.post(`/ads/${id}/duplicate`, data),
};
export const pageAPI = {
  getAll: (params) => api.get('/pages', { params }),
  getAllAdmin: () => api.get('/pages/admin/all'),
  getBySlug: (slug) => api.get(`/pages/${slug}`),
  getById: (id) => api.get(`/pages/id/${id}`),
  getFooterPages: () => api.get('/pages/footer'),
  create: (data) => api.post('/pages', data),
  update: (id, data) => api.put(`/pages/${id}`, data),
  delete: (id) => api.delete(`/pages/${id}`),
};
export const userAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  getStats: () => api.get('/users/stats'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  changePassword: (id, data) => api.put(`/users/${id}/password`, data),
};
export default api;
